// TelcoTrack — VYKOP R100 / MET / UR as object-type filter items in web tables.
// Structural Hlavní trasa / Dokop is no longer an active type filter.

import {
  VYKOP_OBJECT_TYPE,
  VYKOP_SEGMENT_KIND_FILTER_OPTIONS,
  VYKOP_SEGMENT_KIND_FILTER_LABELS,
  effectiveVykopSegmentKind,
  isVykopSegmentKind,
  isVykopTypeFilterId,
  normalizeObjectTypeForLookup,
  type VykopSegmentKind,
} from '@core/shared-bridge';

export type ObjectTypeFilterOption = { value: string; label: string };

/** Segment kind only (R100 / MET / UR). */
export const VYKOP_TABLE_FILTER_OPTIONS: ReadonlyArray<ObjectTypeFilterOption> = [
  ...VYKOP_SEGMENT_KIND_FILTER_OPTIONS,
];

/** Replace `VYKOP` in type multi-select with R100 / MET / UR. */
export function buildObjectTypeFilterOptions(
  options: readonly ObjectTypeFilterOption[],
): ObjectTypeFilterOption[] {
  const out: ObjectTypeFilterOption[] = [];
  for (const opt of options) {
    if (normalizeObjectTypeForLookup(opt.value) === VYKOP_OBJECT_TYPE) {
      for (const v of VYKOP_TABLE_FILTER_OPTIONS) {
        out.push({ value: v.value, label: v.label });
      }
    } else {
      out.push(opt);
    }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, 'cs'));
}

/** Map table filter ids to API object types (`R100` / `MET` / `UR` / legacy structural → `VYKOP`). */
export function expandObjectTypeFilterForApi(selectedTypes: readonly string[]): string[] {
  const apiTypes = new Set<string>();
  for (const t of selectedTypes) {
    if (isVykopSegmentKind(t) || isVykopTypeFilterId(t)) {
      apiTypes.add(VYKOP_OBJECT_TYPE);
    } else {
      apiTypes.add(normalizeObjectTypeForLookup(String(t ?? '').trim()));
    }
  }
  return [...apiTypes];
}

/** Workflow / phase lookups: kind filter ids resolve to `VYKOP`. */
export function resolveWorkflowTypesFromFilter(selectedTypes: readonly string[]): string[] {
  return expandObjectTypeFilterForApi(selectedTypes);
}

/**
 * Legacy URL/state: expand bare `VYKOP` to R100 / MET / UR.
 * Drops legacy `hlavni_trasa` / `dokop`; keeps `R100` / `MET` / `UR` as-is.
 */
export function normalizeObjectTypeFilterSelection(selectedTypes: readonly string[]): string[] {
  const out: string[] = [];
  for (const t of selectedTypes) {
    if (normalizeObjectTypeForLookup(String(t ?? '').trim()) === VYKOP_OBJECT_TYPE) {
      for (const v of VYKOP_TABLE_FILTER_OPTIONS) {
        if (!out.includes(v.value)) out.push(v.value);
      }
    } else if (isVykopTypeFilterId(t)) {
      // Legacy structural selection → all kinds (metric removed).
      for (const v of VYKOP_TABLE_FILTER_OPTIONS) {
        if (!out.includes(v.value)) out.push(v.value);
      }
    } else if (t && !out.includes(t)) {
      out.push(t);
    }
  }
  return out;
}

export interface VykopVariantLookupRow {
  type?: string | null;
  objectType?: string | null;
  metadata?: unknown;
  objectMetadata?: unknown;
}

/** Resolve metadata from row fields (`metadata` or `objectMetadata`). */
export function vykopVariantLookupFromRow(row: VykopVariantLookupRow): {
  type?: string | null;
  metadata?: unknown;
} {
  return {
    type: row.type ?? row.objectType ?? null,
    metadata: row.objectMetadata ?? row.metadata,
  };
}

/** Match row against object-type multi-select (segment-kind pseudo-types). */
export function rowMatchesObjectTypeFilter(
  row: VykopVariantLookupRow,
  selectedTypes: readonly string[],
): boolean {
  if (selectedTypes.length === 0) return true;
  const lookup = vykopVariantLookupFromRow(row);
  const type = normalizeObjectTypeForLookup(String(lookup.type ?? '').trim());

  for (const sel of selectedTypes) {
    if (isVykopSegmentKind(sel)) {
      if (type === VYKOP_OBJECT_TYPE && effectiveVykopSegmentKind(lookup) === sel) return true;
    } else if (isVykopTypeFilterId(sel)) {
      // Legacy structural ids: treat as match-any VYKOP (metric removed).
      if (type === VYKOP_OBJECT_TYPE) return true;
    } else if (normalizeObjectTypeForLookup(String(sel ?? '').trim()) === type) {
      return true;
    }
  }
  return false;
}

export function filterRowsByObjectTypeFilter<T extends VykopVariantLookupRow>(
  rows: readonly T[],
  selectedTypes: readonly string[],
): T[] {
  if (selectedTypes.length === 0) return [...rows];
  return rows.filter((row) => rowMatchesObjectTypeFilter(row, selectedTypes));
}

/** Label for type filter option value (segment kind or API object type). */
export function resolveObjectTypeFilterLabel(
  type: string,
  resolveApiTypeLabel: (apiType: string) => string,
): string {
  if (isVykopSegmentKind(type)) return VYKOP_SEGMENT_KIND_FILTER_LABELS[type as VykopSegmentKind];
  if (isVykopTypeFilterId(type)) {
    // Legacy structural labels no longer shown as active metrics.
    return VYKOP_OBJECT_TYPE;
  }
  return resolveApiTypeLabel(type);
}
